import React, { useState, useEffect } from "react";
import { watchPredictions } from "../../Config/matudb";
import {
  Calendar,
  Clock,
  Award,
  TrendingUp,
  BarChart,
  History,
  ArrowLeft,
  Home,
  Trophy,
  Target,
  Percent,
  ArrowDownAZ,
  Filter,
  ChevronDown,
  Star,
  CheckCircle,
  XCircle,
  Shield,
  Bookmark,
  Crown,
  Search,
  X,
  Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Función para formatear la fecha con mejor formato
const formatDate = (date) => {
  if (!date) return "Fecha no disponible";
  
  try {
    // Si ya está en formato DD/MM/YYYY, lo mantenemos
    if (typeof date === "string" && date.includes("/")) {
      const parts = date.split("/");
      if (parts.length === 3) {
        return date;
      }
    }

    // Convertir a fecha y formatear
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "Fecha inválida";
    
    const options = {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "America/Bogota",
    };
    return dateObj.toLocaleDateString("es-ES", options);
  } catch (error) {
    return "Fecha no disponible";
  }
};

// Función para formatear la hora
const formatTime = (time) => {
  if (!time) return "";
  return time.toString().padStart(2, '0') + ":00";
};

// Función para calcular el tiempo transcurrido
const getTimeAgo = (date) => {
  if (!date) return "";
  
  try {
    const dateObj = new Date(date);
    const now = new Date();
    const diffMs = now - dateObj;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffDays > 0) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    if (diffHours > 0) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    return "Hace poco";
  } catch (error) {
    return "";
  }
};

// Componente para métricas de rendimiento mejorado
const PerformanceMetrics = ({ history }) => {
  // Calcular estadísticas avanzadas
  const totalBets = history.length;
  const won = history.filter((bet) => bet.state === "ganada").length;
  const lost = history.filter((bet) => bet.state === "perdida").length;
  const nulled = history.filter((bet) => bet.state === "nula").length;
  const winRate = totalBets > 0 ? Math.round((won / totalBets) * 100) : 0;

  // Calcular rentabilidad mejorada
  const profitability = history.reduce((total, bet) => {
    if (bet.state === "ganada" && bet.odds) {
      return total + (parseFloat(bet.odds) - 1);
    } else if (bet.state === "perdida") {
      return total - 1;
    }
    return total;
  }, 0);

  const profitabilityPercent =
    totalBets > 0 ? Math.round((profitability / totalBets) * 100) : 0;

  // Calcular racha actual
  const getCurrentStreak = () => {
    let streak = 0;
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].state === "ganada") {
        streak++;
      } else if (history[i].state === "perdida") {
        break;
      }
    }
    return streak;
  };

  const currentStreak = getCurrentStreak();

  const metrics = [
    {
      title: "Total Pronósticos",
      value: totalBets,
      subtitle: `${won + lost + nulled} finalizados`,
      icon: <Target className="h-5 w-5" />,
      color: "text-blue-400",
      bgColor: "bg-blue-900/20",
      borderColor: "border-blue-500/30",
    },
    {
      title: "Victorias",
      value: won,
      subtitle: `${winRate}% efectividad`,
      icon: <Trophy className="h-5 w-5" />,
      color: "text-green-400",
      bgColor: "bg-green-900/20",
      borderColor: "border-green-500/30",
    },
    {
      title: "Racha Actual",
      value: currentStreak,
      subtitle: currentStreak > 0 ? "¡En racha!" : "Sin racha",
      icon: <TrendingUp className="h-5 w-5" />,
      color: "text-yellow-400",
      bgColor: "bg-yellow-900/20",
      borderColor: "border-yellow-500/30",
    },
    {
      title: "Rentabilidad",
      value: `${profitabilityPercent > 0 ? "+" : ""}${profitabilityPercent}%`,
      subtitle: profitabilityPercent >= 0 ? "Ganando" : "Perdiendo",
      icon: <Percent className="h-5 w-5" />,
      color: profitabilityPercent >= 0 ? "text-green-400" : "text-red-400",
      bgColor: profitabilityPercent >= 0 ? "bg-green-900/20" : "bg-red-900/20",
      borderColor: profitabilityPercent >= 0 ? "border-green-500/30" : "border-red-500/30",
    },
  ];

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-gray-300 mb-4 flex items-center">
        <BarChart className="w-5 h-5 mr-2 text-blue-400" />
        Métricas de Rendimiento
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className={`${metric.bgColor} ${metric.borderColor} rounded-xl p-4 shadow-lg border transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-300 font-medium text-sm">
                {metric.title}
              </span>
              <div className={`${metric.color} bg-gray-900/50 p-2 rounded-lg backdrop-blur-sm`}>
                {metric.icon}
              </div>
            </div>
            <div className="text-white text-2xl font-bold mb-1">{metric.value}</div>
            <div className="text-gray-400 text-xs">{metric.subtitle}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Componente para cada tarjeta de historial mejorado
const BetHistoryCard = ({ prediction }) => {
  // Determinar el color del estado con mejor diseño
  let statusColor, statusText, statusIcon, statusBg, statusGlow;
  switch (prediction.state) {
    case "ganada":
      statusColor = "text-green-400";
      statusBg = "bg-green-900/80";
      statusGlow = "shadow-green-500/50";
      statusText = "Ganada";
      statusIcon = <Award className="w-4 h-4" />;
      break;
    case "perdida":
      statusColor = "text-red-400";
      statusBg = "bg-red-900/80";
      statusGlow = "shadow-red-500/50";
      statusText = "Perdida";
      statusIcon = <XCircle className="w-4 h-4" />;
      break;
    case "nula":
      statusColor = "text-gray-400";
      statusBg = "bg-gray-700/80";
      statusGlow = "shadow-gray-500/50";
      statusText = "Nula";
      statusIcon = <BarChart className="w-4 h-4" />;
      break;
    default:
      statusColor = "text-gray-400";
      statusBg = "bg-gray-700/80";
      statusGlow = "shadow-gray-500/50";
      statusText = "Desconocido";
      statusIcon = <History className="w-4 h-4" />;
  }

  // Nivel de confianza
  const confidenceLevel = prediction.confidence || 0;
  let confidenceColor;
  if (confidenceLevel >= 80) {
    confidenceColor = "bg-green-500";
  } else if (confidenceLevel >= 60) {
    confidenceColor = "bg-green-600";
  } else if (confidenceLevel >= 40) {
    confidenceColor = "bg-yellow-500";
  } else if (confidenceLevel >= 20) {
    confidenceColor = "bg-orange-500";
  } else {
    confidenceColor = "bg-red-500";
  }

  // Determinar si es VIP o Free
  const isVip = prediction.source === "abetvip";
  const planBadge = isVip ? (
    <div className="absolute top-3 left-3 bg-yellow-900 text-yellow-300 px-3 py-1 rounded-lg flex items-center space-x-1 shadow-md">
      <Crown className="w-4 h-4" />
      <span className="text-sm font-medium ml-1">VIP</span>
    </div>
  ) : (
    <div className="absolute top-3 left-3 bg-blue-900 text-blue-300 px-3 py-1 rounded-lg flex items-center space-x-1 shadow-md">
      <Bookmark className="w-4 h-4" />
      <span className="text-sm font-medium ml-1">FREE</span>
    </div>
  );

  return (
    <div className="w-full bg-gray-800 rounded-xl shadow-lg mb-4 overflow-hidden hover:shadow-blue-900/20 transition-all duration-300 border border-gray-700 hover:border-gray-600">
      <div className="relative">
        {/* Indicador de Plan (VIP o Free) */}
        {planBadge}

        {/* Indicador de Estado mejorado */}
        <div
          className={`absolute top-3 right-3 ${statusBg} ${statusColor} px-3 py-1 rounded-lg flex items-center space-x-1 shadow-md backdrop-blur-sm border border-white/10 ${statusGlow}`}
        >
          {statusIcon}
          <span className="text-sm font-medium ml-1">{statusText}</span>
        </div>

        {/* Contenido Principal */}
        <div className="p-5">
          {/* Equipos con logos */}
          <div className="flex items-center justify-between mb-6 mt-8">
            <div className="flex items-center flex-1">
              <div className="bg-gray-700 rounded-full p-2 mr-3 shadow-md">
                {prediction.homeTeam?.logo ? (
                  <img
                    src={prediction.homeTeam.logo}
                    alt={prediction.homeTeam.name}
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <Shield className="w-10 h-10 text-gray-500" />
                )}
              </div>
              <span className="font-semibold text-white">
                {prediction.homeTeam?.name || "Equipo Local"}
              </span>
            </div>

            <div className="mx-4 text-gray-400 font-bold text-lg">VS</div>

            <div className="flex items-center flex-1 justify-end">
              <span className="font-semibold text-white">
                {prediction.awayTeam?.name || "Equipo Visitante"}
              </span>
              <div className="bg-gray-700 rounded-full p-2 ml-3 shadow-md">
                {prediction.awayTeam?.logo ? (
                  <img
                    src={prediction.awayTeam.logo}
                    alt={prediction.awayTeam.name}
                    className="w-10 h-10 object-contain"
                  />
                ) : (
                  <Shield className="w-10 h-10 text-gray-500" />
                )}
              </div>
            </div>
          </div>

          {/* Fecha, Hora y Cuota mejorada */}
          <div className="flex justify-between items-center mb-5 text-sm">
            <div className="flex flex-col space-y-1">
              <div className="flex items-center text-gray-400">
                <Calendar className="w-4 h-4 mr-1" />
                <span>{formatDate(prediction.date)}</span>
                {prediction.hours && (
                  <>
                    <Clock className="w-4 h-4 ml-3 mr-1" />
                    <span>{formatTime(prediction.hours)}</span>
                  </>
                )}
              </div>
              <div className="text-xs text-gray-500 ml-5">
                {getTimeAgo(prediction.date)}
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-blue-300 font-bold rounded-lg px-4 py-2 flex items-center shadow-lg border border-blue-700/50">
              <Star className="w-4 h-4 mr-1 text-yellow-400" />
              <span className="text-sm">
                {typeof prediction.odds === "number"
                  ? prediction.odds.toFixed(2)
                  : prediction.odds || "1.00"}
              </span>
            </div>
          </div>

          {/* Predicción mejorada */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-4 mb-4 border border-gray-700 shadow-inner">
            <div className="font-medium text-gray-400 mb-2 flex items-center">
              <Target className="w-4 h-4 mr-2 text-blue-400" />
              Predicción:
            </div>
            <div className="text-white font-bold text-lg bg-gray-800 p-3 rounded-lg border border-gray-600">
              {prediction.prediction || "No disponible"}
            </div>
          </div>

          {/* Nivel de confianza mejorado */}
          {typeof prediction.confidence === "number" && (
            <div className="mt-4">
              <div className="flex justify-between mb-3">
                <span className="text-sm font-medium text-gray-400 flex items-center">
                  <Shield className="w-4 h-4 mr-2 text-blue-400" />
                  Nivel de Confianza
                </span>
                <span className="text-sm font-bold text-white bg-gray-800 px-2 py-1 rounded-lg">
                  {prediction.confidence}%
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3 shadow-inner">
                <div
                  className={`${confidenceColor} h-3 rounded-full shadow-lg transition-all duration-700 ease-out relative overflow-hidden`}
                  style={{ width: `${prediction.confidence}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                </div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Baja</span>
                <span>Media</span>
                <span>Alta</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente principal
const BetHistoryDashboard = ({ userUUID }) => {
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [activePlan, setActivePlan] = useState("all"); // Nuevo estado para el filtro de plan
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  // Función para exportar historial
  const exportHistory = () => {
    const filteredData = getFilteredHistory();
    const csvContent = [
      ['Fecha', 'Equipo Local', 'Equipo Visitante', 'Predicción', 'Estado', 'Cuota', 'Confianza', 'Plan'],
      ...filteredData.map(prediction => [
        formatDate(prediction.date),
        prediction.homeTeam?.name || 'N/A',
        prediction.awayTeam?.name || 'N/A',
        prediction.prediction || 'N/A',
        prediction.state,
        prediction.odds || 'N/A',
        prediction.confidence ? `${prediction.confidence}%` : 'N/A',
        prediction.source === 'abetvip' ? 'VIP' : 'FREE'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historial_pronosticos_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Cerrar menú de ordenamiento cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSortMenu && !event.target.closest('.sort-menu-container')) {
        setShowSortMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortMenu]);

  // Resetear a la primera página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, activePlan, searchTerm, sortBy]);

  useEffect(() => {
    setIsLoading(true);
    let freeRows = null;
    let vipRows = null;

    const finished = (rows) => {
      const scoped = userUUID
        ? rows.filter((prediction) => prediction.userUUID === userUUID)
        : rows;
      return scoped.filter(
        (prediction) =>
          prediction.state === "ganada" ||
          prediction.state === "perdida" ||
          prediction.state === "nula"
      );
    };

    const publish = () => {
      if (!freeRows || !vipRows) return;
      setHistory([...freeRows, ...vipRows]);
      setIsLoading(false);
    };

    const stopFree = watchPredictions("abet", (rows) => {
      freeRows = finished(rows);
      publish();
    });
    const stopVip = watchPredictions("abetvip", (rows) => {
      vipRows = finished(rows);
      publish();
    });

    return () => {
      stopFree();
      stopVip();
    };
  }, [userUUID]);

  // Función para filtrar el historial según la pestaña activa, plan activo y término de búsqueda
  const getFilteredHistory = () => {
    let filtered = [...history];

    // Filtrar por término de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((prediction) => {
        const homeTeam = prediction.homeTeam?.name?.toLowerCase() || '';
        const awayTeam = prediction.awayTeam?.name?.toLowerCase() || '';
        const predictionText = prediction.prediction?.toLowerCase() || '';
        
        return homeTeam.includes(term) || 
               awayTeam.includes(term) || 
               predictionText.includes(term);
      });
    }

    // Filtrar por estado (ganada, perdida, nula)
    switch (activeTab) {
      case "won":
        filtered = filtered.filter(
          (prediction) => prediction.state === "ganada"
        );
        break;
      case "lost":
        filtered = filtered.filter(
          (prediction) => prediction.state === "perdida"
        );
        break;
      case "null":
        filtered = filtered.filter((prediction) => prediction.state === "nula");
        break;
      // Eliminamos el caso "pending" ya que no queremos mostrar los pendientes
    }

    // Filtrar por plan (VIP o FREE)
    switch (activePlan) {
      case "free":
        filtered = filtered.filter(
          (prediction) => prediction.source === "abet"
        );
        break;
      case "vip":
        filtered = filtered.filter(
          (prediction) => prediction.source === "abetvip"
        );
        break;
      // El caso "all" no necesita filtrado adicional
    }

    // Ordenar por fecha
    return filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (sortBy === "newest") {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });
  };

  // Función para obtener los datos paginados
  const getPaginatedData = () => {
    const filteredData = getFilteredHistory();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  // Calcular el total de páginas
  const totalPages = Math.ceil(getFilteredHistory().length / itemsPerPage);

  // Función para cambiar de página
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll hacia arriba para mejor UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Función para ir a la primera página
  const goToFirstPage = () => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Función para ir a la última página
  const goToLastPage = () => {
    setCurrentPage(totalPages);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Función para ir a la página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Función para ir a la página siguiente
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Obtener el conteo para cada categoría
  const counts = {
    all: history.length,
    won: history.filter((prediction) => prediction.state === "ganada").length,
    lost: history.filter((prediction) => prediction.state === "perdida").length,
    null: history.filter((prediction) => prediction.state === "nula").length,
    free: history.filter((prediction) => prediction.source === "abet").length,
    vip: history.filter((prediction) => prediction.source === "abetvip").length,
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header mejorado */}
      <header className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-gray-700 shadow-2xl sticky top-0 z-10 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={handleBack}
                className="mr-4 bg-gray-800 hover:bg-blue-600 rounded-full p-3 transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-blue-500/25"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-full mr-3 shadow-lg">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">DeportivosPro</h1>
                  <p className="text-xs text-gray-400">Historial de Pronósticos</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleBack}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-full p-3 transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-blue-500/25"
            >
              <Home className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto p-4">
        {/* Titulo y filtros */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 mt-4">
          <h2 className="text-2xl font-bold text-white flex items-center mb-4 md:mb-0">
            <History className="w-6 h-6 mr-2 text-blue-400" />
            Historial de Pronósticos
          </h2>

          <div className="flex items-center space-x-3">
            {/* Botón de exportar */}
            {!isLoading && history.length > 0 && (
              <button
                onClick={exportHistory}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-4 py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Exportar</span>
              </button>
            )}

            {/* Botón de ordenar mejorado */}
            <div className="relative sort-menu-container">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center space-x-2 bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 text-white px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-600 hover:border-gray-500 transform hover:scale-105"
              >
                <ArrowDownAZ className="w-4 h-4" />
                <span className="font-medium">
                  Ordenar por:{" "}
                  {sortBy === "newest" ? "Más recientes" : "Más antiguos"}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showSortMenu ? 'rotate-180' : ''}`} />
              </button>

            {showSortMenu && (
              <div className="absolute right-0 mt-3 w-56 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl shadow-2xl border border-gray-700 z-10 backdrop-blur-sm">
                <div className="p-2">
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-gray-700/50 rounded-lg flex items-center transition-colors duration-200 group"
                    onClick={() => {
                      setSortBy("newest");
                      setShowSortMenu(false);
                    }}
                  >
                    {sortBy === "newest" ? (
                      <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                    ) : (
                      <div className="w-4 h-4 mr-3 rounded-full border-2 border-gray-500 group-hover:border-blue-400 transition-colors duration-200"></div>
                    )}
                    <span className={sortBy === "newest" ? "font-semibold text-white" : "text-gray-300 group-hover:text-white"}>
                      Más recientes
                    </span>
                  </button>
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-gray-700/50 rounded-lg flex items-center transition-colors duration-200 group"
                    onClick={() => {
                      setSortBy("oldest");
                      setShowSortMenu(false);
                    }}
                  >
                    {sortBy === "oldest" ? (
                      <CheckCircle className="w-4 h-4 mr-3 text-green-400" />
                    ) : (
                      <div className="w-4 h-4 mr-3 rounded-full border-2 border-gray-500 group-hover:border-blue-400 transition-colors duration-200"></div>
                    )}
                    <span className={sortBy === "oldest" ? "font-semibold text-white" : "text-gray-300 group-hover:text-white"}>
                      Más antiguos
                    </span>
                  </button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Métricas de rendimiento */}
        <PerformanceMetrics history={history} />

        {/* Indicador de Progreso de Paginación */}
        {/* {!isLoading && totalPages > 1 && (
          <div className="mb-6">
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-300 font-medium text-sm">Progreso de navegación</span>
                <span className="text-gray-400 text-xs">Página {currentPage} de {totalPages}</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500 ease-out shadow-lg"
                  style={{ width: `${(currentPage / totalPages) * 100}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>Inicio</span>
                <span>Final</span>
              </div>
            </div>
          </div>
        )} */}

        {/* Campo de búsqueda */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar por equipo o predicción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 pl-12 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 shadow-lg"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <X className="h-5 w-5 text-gray-400 hover:text-white transition-colors duration-200" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-400 mt-2">
              Buscando: "{searchTerm}" • {getFilteredHistory().length} resultados
            </p>
          )}
        </div>

        {/* Filtro de Plan (VIP/FREE) */}
        <div className="flex overflow-x-auto mb-4 bg-gray-900 rounded-xl shadow-md border border-gray-800">
          <button
            onClick={() => setActivePlan("all")}
            className={`flex-1 py-3 px-4 text-center relative ${
              activePlan === "all"
                ? "text-blue-400 font-bold"
                : "text-gray-400 hover:text-blue-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <Filter className="w-4 h-4 mr-2" />
              <span>Todos</span>
              <span className="ml-2 bg-gray-800 text-gray-300 rounded-full px-2 py-0.5 text-xs">
                {counts.all}
              </span>
            </div>
            {activePlan === "all" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-500 animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActivePlan("free")}
            className={`flex-1 py-3 px-4 text-center relative ${
              activePlan === "free"
                ? "text-blue-400 font-bold"
                : "text-gray-400 hover:text-blue-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <Bookmark className="w-4 h-4 mr-2" />
              <span>FREE</span>
              <span className="ml-2 bg-blue-900 text-blue-300 rounded-full px-2 py-0.5 text-xs">
                {counts.free}
              </span>
            </div>
            {activePlan === "free" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-500 animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActivePlan("vip")}
            className={`flex-1 py-3 px-4 text-center relative ${
              activePlan === "vip"
                ? "text-yellow-400 font-bold"
                : "text-gray-400 hover:text-yellow-300"
            } transition-colors duration-200`}
          >
            <div className="flex items-center justify-center">
              <Crown className="w-4 h-4 mr-2" />
              <span>VIP</span>
              <span className="ml-2 bg-yellow-900 text-yellow-300 rounded-full px-2 py-0.5 text-xs">
                {counts.vip}
              </span>
            </div>
            {activePlan === "vip" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-yellow-500 animate-pulse"></div>
            )}
          </button>
        </div>

        {/* Tabs de Estado mejorados */}
        <div className="flex overflow-x-auto mb-6 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl shadow-xl border border-gray-700 p-1">
          <button
            onClick={() => setActiveTab("all")}
            className={`flex-1 py-4 px-4 text-center relative rounded-lg transition-all duration-300 ${
              activeTab === "all"
                ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold shadow-lg shadow-blue-500/25"
                : "text-gray-400 hover:text-blue-300 hover:bg-gray-800/50"
            }`}
          >
            <div className="flex items-center justify-center">
              <Filter className="w-4 h-4 mr-2" />
              <span>Todos</span>
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                activeTab === "all" 
                  ? "bg-white/20 text-white" 
                  : "bg-gray-800 text-gray-300"
              }`}>
                {counts.all}
              </span>
            </div>
            {activeTab === "all" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab("won")}
            className={`flex-1 py-4 px-4 text-center relative rounded-lg transition-all duration-300 ${
              activeTab === "won"
                ? "bg-gradient-to-r from-green-600 to-green-700 text-white font-bold shadow-lg shadow-green-500/25"
                : "text-gray-400 hover:text-green-300 hover:bg-gray-800/50"
            }`}
          >
            <div className="flex items-center justify-center">
              <CheckCircle className="w-4 h-4 mr-2" />
              <span>Ganados</span>
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                activeTab === "won" 
                  ? "bg-white/20 text-white" 
                  : "bg-green-900 text-green-300"
              }`}>
                {counts.won}
              </span>
            </div>
            {activeTab === "won" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-green-600 rounded-full animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab("lost")}
            className={`flex-1 py-4 px-4 text-center relative rounded-lg transition-all duration-300 ${
              activeTab === "lost"
                ? "bg-gradient-to-r from-red-600 to-red-700 text-white font-bold shadow-lg shadow-red-500/25"
                : "text-gray-400 hover:text-red-300 hover:bg-gray-800/50"
            }`}
          >
            <div className="flex items-center justify-center">
              <XCircle className="w-4 h-4 mr-2" />
              <span>Perdidos</span>
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                activeTab === "lost" 
                  ? "bg-white/20 text-white" 
                  : "bg-red-900 text-red-300"
              }`}>
                {counts.lost}
              </span>
            </div>
            {activeTab === "lost" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-400 to-red-600 rounded-full animate-pulse"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab("null")}
            className={`flex-1 py-4 px-4 text-center relative rounded-lg transition-all duration-300 ${
              activeTab === "null"
                ? "bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold shadow-lg shadow-gray-500/25"
                : "text-gray-400 hover:text-gray-300 hover:bg-gray-800/50"
            }`}
          >
            <div className="flex items-center justify-center">
              <BarChart className="w-4 h-4 mr-2" />
              <span>Nulos</span>
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                activeTab === "null" 
                  ? "bg-white/20 text-white" 
                  : "bg-gray-700 text-gray-300"
              }`}>
                {counts.null}
              </span>
            </div>
            {activeTab === "null" && (
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full animate-pulse"></div>
            )}
          </button>
        </div>

        {/* Estadísticas en tiempo real */}
        {!isLoading && history.length > 0 && (
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-4 mb-6 border border-gray-700 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{getFilteredHistory().length}</div>
                  <div className="text-xs text-gray-400">Total Resultados</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {getFilteredHistory().filter(p => p.state === "ganada").length}
                  </div>
                  <div className="text-xs text-gray-400">Ganados</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {getFilteredHistory().filter(p => p.state === "perdida").length}
                  </div>
                  <div className="text-xs text-gray-400">Perdidos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {totalPages}
                  </div>
                  <div className="text-xs text-gray-400">Páginas</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400">Página {currentPage} de {totalPages}</div>
                <div className="text-xs text-gray-500">Mostrando {getPaginatedData().length} de {getFilteredHistory().length}</div>
              </div>
            </div>
          </div>
        )}

        {/* Indicador de Paginación Rápida */}
        {!isLoading && totalPages > 1 && (
          <div className="mb-6">
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-400 text-sm font-medium">Navegación rápida:</span>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(7, totalPages) }, (_, index) => {
                      let pageNumber;
                      if (totalPages <= 7) {
                        pageNumber = index + 1;
                      } else if (currentPage <= 4) {
                        pageNumber = index + 1;
                      } else if (currentPage >= totalPages - 3) {
                        pageNumber = totalPages - 6 + index;
                      } else {
                        pageNumber = currentPage - 3 + index;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`w-8 h-8 text-xs font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                            currentPage === pageNumber
                              ? 'bg-blue-600 text-white border border-blue-500 shadow-lg'
                              : 'text-gray-400 bg-gray-700 border border-gray-600 hover:bg-gray-600 hover:text-white'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                  {totalPages > 7 && (
                    <span className="text-gray-500 text-xs">...</span>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-xs font-medium text-gray-400 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    ← Anterior
                  </button>
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-xs font-medium text-gray-400 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Siguiente →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resultados mejorados */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-700 border-t-blue-500"></div>
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
              </div>
              <p className="text-gray-400 mt-4 text-lg">Cargando historial...</p>
              <div className="flex space-x-2 mt-4">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          ) : getFilteredHistory().length > 0 ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-400 text-sm">
                  Mostrando {getPaginatedData().length} de {getFilteredHistory().length} pronósticos
                </p>
                <div className="text-gray-400 text-sm">
                  {activeTab !== "all" && `${activeTab === "won" ? "Ganados" : activeTab === "lost" ? "Perdidos" : "Nulos"}`}
                  {activePlan !== "all" && ` - ${activePlan === "vip" ? "VIP" : "FREE"}`}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getPaginatedData().map((prediction) => (
                  <BetHistoryCard key={prediction.id} prediction={prediction} />
                ))}
              </div>

              {/* Componente de Paginación */}
              {totalPages > 1 && (
                <div className="mt-8">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-400">
                      Página {currentPage} de {totalPages} • {getFilteredHistory().length} pronósticos totales
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Botón Primera Página */}
                      <button
                        onClick={goToFirstPage}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Primera
                      </button>

                      {/* Botón Página Anterior */}
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Anterior
                      </button>

                      {/* Números de Página */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                          let pageNumber;
                          if (totalPages <= 5) {
                            pageNumber = index + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = index + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + index;
                          } else {
                            pageNumber = currentPage - 2 + index;
                          }

                          return (
                            <button
                              key={pageNumber}
                              onClick={() => handlePageChange(pageNumber)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                currentPage === pageNumber
                                  ? 'bg-blue-600 text-white border border-blue-500'
                                  : 'text-gray-400 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:text-white'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>

                      {/* Botón Página Siguiente */}
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Siguiente
                      </button>

                      {/* Botón Última Página */}
                      <button
                        onClick={goToLastPage}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-400 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        Última
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-16 rounded-2xl shadow-2xl text-center border border-gray-700">
              <div className="bg-gray-700 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <History className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-gray-300 text-xl font-semibold mb-3">
                No hay pronósticos en esta categoría
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Los pronósticos aparecerán aquí cuando estén disponibles. Intenta cambiar los filtros o esperar a que se agreguen nuevos pronósticos.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setActiveTab("all")}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                >
                  Ver Todos
                </button>
                <button
                  onClick={() => setActivePlan("all")}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                >
                  Todos los Planes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BetHistoryDashboard;
